/**
 * personalizationEngine.js — Feb 2026 · Emergent Command · Defensibility.
 * ──────────────────────────────────────────────────────────────
 * Thin client wrapping our Claude-backed personalization endpoint.
 * Ships CAPABILITY 1 · "Explain another way" today; the same client
 * will fan out to dynamic-practice + path-reasoning next turn via
 * `mode=` param.
 *
 * Design principles (from the Emergent Command spec):
 *   • Never block the child on failure — every call resolves to a
 *     shape the caller can render or gracefully ignore.
 *   • Non-PII in and out — we thread the anonymous analytics id and
 *     compact skill/subject signals only.
 *   • Client-side cache (7-day) + validation on every response so
 *     stale/unsafe content never re-renders.
 *   • Fires the three analytics events called out in the spec:
 *     personalization_requested, personalization_served,
 *     personalization_fallback.
 */
import { getAnonId, trackEvent } from "@/lib/analytics";
import { deriveLearnerProfile } from "@/lib/learnerProfile";

const API = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = "we_personalize_cache_v1";
const CACHE_TTL_MS = 7 * 24 * 3600 * 1000;

function _cacheRead() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "{}"); }
  catch { return {}; }
}
function _cacheWrite(store) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(store)); } catch { /* quota */ }
}
function _cacheKey({ skill, subject, ageTier }) {
  return `${(skill || "").toLowerCase()}|${(subject || "").toLowerCase()}|${ageTier || "explorer"}`;
}

/**
 * Ask Claude (via server) to re-explain a concept in Compass's voice.
 * Returns { ok, fallback, data, reason }. Never throws.
 */
export async function explainAnotherWay({ skill, subject = "", ageTier = "explorer", sectionText = "", whatWentWrong = "" } = {}) {
  if (!skill) {
    return { ok: false, fallback: true, reason: "no-skill", data: null };
  }
  // Trim + cap section context so we never send big payloads.
  const trimmed = String(sectionText || "").slice(0, 1500);

  // 1. Client-side cache lookup.
  const key = _cacheKey({ skill, subject, ageTier });
  const store = _cacheRead();
  const hit = store[key];
  if (hit && (Date.now() - hit.ts) < CACHE_TTL_MS) {
    trackEvent("personalization_served", { skill, subject, source: "cache" });
    return { ok: true, fallback: false, reason: "cache", data: hit.data };
  }

  // 2. Fire the "requested" event so we can measure demand later
  // even when the server declines.
  trackEvent("personalization_requested", { skill, subject, age_tier: ageTier });

  let profile;
  try { profile = deriveLearnerProfile(); } catch { profile = null; }

  // 3. Server call.
  try {
    const res = await fetch(`${API}/api/personalize/explain`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        anon_id: getAnonId(),
        mode: "explain",
        skill: String(skill).slice(0, 80),
        subject: String(subject || "").slice(0, 40),
        age_tier: ageTier,
        section_text: trimmed,
        what_went_wrong: String(whatWentWrong || "").slice(0, 200),
        // Profile is passed as a compact `struggles` hint for logging
        // — the server does not currently forward it to the model.
        _profile_hint: profile ? { top_struggle: profile.topStruggle || null, streak: profile.streak || 0 } : null,
      }),
    });
    const json = await res.json();
    if (!json || !json.ok) {
      trackEvent("personalization_fallback", {
        skill, subject, reason: json?.reason || "no-response",
      });
      return { ok: false, fallback: true, reason: json?.reason || "server-fail", data: null };
    }
    // Cache validated data + trim store to last 40 entries.
    store[key] = { ts: Date.now(), data: json.data };
    const keys = Object.keys(store);
    if (keys.length > 40) {
      const stale = keys
        .map((k) => [k, store[k].ts || 0])
        .sort((a, b) => a[1] - b[1])
        .slice(0, keys.length - 40)
        .map(([k]) => k);
      stale.forEach((k) => delete store[k]);
    }
    _cacheWrite(store);
    trackEvent("personalization_served", { skill, subject, source: "llm" });
    return { ok: true, fallback: false, reason: "generated", data: json.data };
  } catch (err) {
    trackEvent("personalization_fallback", { skill, subject, reason: "network" });
    return { ok: false, fallback: true, reason: "network", data: null };
  }
}

/** After displaying an explanation, the child can tap "That helped".
 *  Emits the defensibility metric — did adaptive help unstick them? */
export function recordPersonalizationHelpful({ skill, subject, verdict }) {
  trackEvent("personalization_helpful", {
    skill: String(skill || "").slice(0, 80),
    subject: String(subject || "").slice(0, 40),
    verdict: verdict === true ? "helped" : verdict === false ? "didn_t_help" : "unknown",
  });
}
